// Result payloads deliberately exclude schedule metadata and never calculate official places.
export const copy = value => JSON.parse(JSON.stringify(value))
const number = value => value === '' || value == null ? null : Number(value)
export function resultState(row) {
  if (['DNS', 'DNF', 'DSQ'].includes(row.resultStatus)) return row.resultStatus
  const marker = String(row.resultTime || '').trim().toUpperCase()
  return ['DNS', 'DNF', 'DSQ'].includes(marker) ? marker : marker === '犯规' ? 'DSQ' : 'FINISHED'
}
export function editableRow(row) {
  const status = resultState(row)
  return { ...copy(row), resultStatus: status, rankNo: status === 'FINISHED' && row.rankNo > 0 ? Number(row.rankNo) : null,
    resultTime: status === 'FINISHED' ? row.resultTime || '' : '', laneNo: number(row.laneNo), bibNo: row.bibNo || '', remark: row.remark || '' }
}
export function rowValues(row) {
  const status = resultState(row)
  return { eventEntryId: number(row.eventEntryId), laneNo: number(row.laneNo), bibNo: String(row.bibNo || '').trim(),
    resultStatus: status, resultTime: status === 'FINISHED' ? String(row.resultTime || '').trim() : '',
    rankNo: status === 'FINISHED' && row.rankNo > 0 ? Number(row.rankNo) : null, remark: String(row.remark || '').trim() }
}
export const valuesStamp = rows => JSON.stringify(rows.map(rowValues))
export const revisionStamp = rows => JSON.stringify(rows.map(row => [row.id, row.resultRevision == null ? 0 : row.resultRevision, row.published]).sort((a, b) => a[0] - b[0]))
export function batchPayload(round, rows, reason = '') {
  return { roundId: round.id, correctionReason: reason.trim(), results: rows.map((row, index) => ({
    ...rowValues(row), id: row.id, resultRevision: row.resultRevision, eventId: round.eventId, categoryId: round.categoryId,
    roundId: round.id, published: 0, sortOrder: index + 1,
    // A changed time makes an old stored gap misleading. It is derived on screen instead.
    gapTime: ''
  })) }
}
export function timeMillis(value) {
  const text = String(value || '').trim()
  if (!/^(?:\d{1,3}:)?\d{1,3}(?:\.\d{1,3})?$/.test(text)) return null
  const parts = text.split(':'), seconds = parts.pop().split('.')
  if (parts.length && Number(seconds[0]) >= 60) return null
  const total = (Number(parts[0] || 0) * 60 + Number(seconds[0])) * 1000 + Number((seconds[1] || '').padEnd(3, '0'))
  return total > 0 ? total : null
}
export function validateRows(rows, publishing = false) {
  const entries = new Set(), lanes = new Set()
  if (publishing && !rows.length) return 'empty'
  for (const row of rows) {
    if (!row.eventEntryId) return 'entryRequired'
    if (entries.has(Number(row.eventEntryId))) return 'duplicateEntry'
    entries.add(Number(row.eventEntryId))
    const lane = number(row.laneNo)
    if (lane != null && (!Number.isInteger(lane) || lane < 1)) return 'invalidLane'
    if (lane != null && lanes.has(lane)) return 'duplicateLane'
    if (lane != null) lanes.add(lane)
    if (row.rankNo != null && row.rankNo !== '' && (!Number.isInteger(Number(row.rankNo)) || Number(row.rankNo) < 1)) return 'invalidRank'
    if (resultState(row) === 'FINISHED') {
      if (String(row.resultTime || '').trim() && timeMillis(row.resultTime) == null) return 'invalidTime'
      if (publishing && !timeMillis(row.resultTime) && !(row.rankNo > 0)) return 'incomplete'
    }
    if (String(row.bibNo || '').length > 20 || String(row.remark || '').length > 255) return 'tooLong'
  }
  return ''
}
export function orderedRows(rows) {
  return rows.slice().sort((a, b) => (a.sortOrder || a.laneNo || 999999) - (b.sortOrder || b.laneNo || 999999) || a.id - b.id)
}
// Fetch every page; a truncated page must never become the basis for a batch write/publish.
export async function allPages(get, url, query) {
  const rows = [], seen = new Set()
  let page = 1, total = 0
  do {
    const response = await get(url, { ...query, page, pageSize: 500 })
    const list = response.data.list || []
    total = Number(response.data.total) || list.length
    if ((!list.length && rows.length < total) || list.some(row => seen.has(row.id))) throw new Error('Incomplete or changing result pages; refresh and review again')
    list.forEach(row => seen.add(row.id))
    rows.push(...list)
    page++
  } while (rows.length < total)
  return rows
}

const labels = {
  saveAll: ['保存全部草稿', '儲存全部草稿', 'Save changed drafts'], saveRace: ['保存本场草稿', '儲存本場草稿', 'Save race draft'],
  dirty: ['未保存', '未儲存', 'Unsaved'], saved: ['草稿已保存', '草稿已儲存', 'Draft saved'], saving: ['保存中', '儲存中', 'Saving'],
  review: ['复核发布', '覆核發布', 'Review & publish'], details: ['详情与历史', '詳情與歷史', 'Details & history'],
  correct: ['更正成绩', '更正成績', 'Correct results'], saveCorrection: ['保存更正草稿', '儲存更正草稿', 'Save correction draft'],
  correctionNotice: ['整场退回待复核；相关汇总需重新核对。历史记录保留，保存不等于发布。', '整場退回待覆核；相關彙總需重新核對。歷史記錄保留，儲存不等於發布。', 'The race returns to review. Recheck derived classifications. History is retained; saving does not publish.'],
  version: ['版本', '版本', 'Revision'], history: ['审计记录', '審計記錄', 'Audit history'], historyLimit: ['仅展示每条成绩最近200条审计。', '僅展示每條成績最近200條審計。', 'Up to 200 recent audit entries per result.'],
  allCategories: ['全部组别', '全部組別', 'All categories'], allStages: ['全部阶段', '全部階段', 'All stages'], allStates: ['全部状态', '全部狀態', 'All states'],
  allDistances: ['全部距离', '全部距離', 'All distances'], more: ['更多', '更多', 'More'], cancel: ['取消', '取消', 'Cancel'],
  count: ['队伍', '隊伍', 'entries'], gap: ['距最快', '距最快', 'Gap to fastest'], order: ['拖动只调整显示顺序，不改变名次或航道', '拖動只調整顯示順序，不改變名次或航道', 'Drag changes display order only, never place or lane'],
  expand: ['展开', '展開', 'Expand'], collapse: ['收起', '收起', 'Collapse'], empty: ['暂无对阵行', '暫無對陣行', 'No draw rows'], noRaces: ['当前筛选下暂无场次', '目前篩選下暫無場次', 'No races match these filters'],
  leave: ['有未保存的输入，确定放弃并离开？', '有未儲存的輸入，確定放棄並離開？', 'Discard unsaved input and leave?'],
  discard: ['放弃本场输入并读取最新记录？其他场次的输入会保留。', '放棄本場輸入並讀取最新記錄？其他場次的輸入會保留。', 'Discard this race’s input and read current records? Other race drafts are retained.'],
  saveFirst: ['请先保存本场草稿，再复核或执行此操作。', '請先儲存本場草稿，再覆核或執行此操作。', 'Save this race’s draft before reviewing or continuing.'],
  importFirst: ['请先保存全部草稿，再导入或新增场次。', '請先儲存全部草稿，再匯入或新增場次。', 'Save all drafts before importing or adding a race.'],
  conflict: ['记录已有变化。输入已保留；请读取最新记录后重新核对，不能覆盖保存。', '記錄已有變化。輸入已保留；請讀取最新記錄後重新核對，不能覆蓋儲存。', 'Records changed. Your input is retained. Read current records and review; overwrite is blocked.'],
  uncertain: ['提交结果待确认，已阻止重复提交。请先读取最新记录核对。', '提交結果待確認，已阻止重複提交。請先讀取最新記錄核對。', 'Submission outcome is uncertain. Retry is blocked until current records are checked.'],
  freshReview: ['发布未完成。必须关闭后重新打开复核，不可重复提交旧版本。', '發布未完成。必須關閉後重新開啟覆核，不可重複提交舊版本。', 'Publication not confirmed. Close and reopen review before trying again.'],
  checked: ['我已逐项核对裁判成绩单', '我已逐項核對裁判成績單', 'I checked every row against the judges’ sheet'],
  done: ['保存成功', '儲存成功', 'Saved'], failedCount: ['未完成，输入保留', '未完成，輸入保留', 'Not saved; input retained'],
  entryRequired: ['请为每行选择本组别参赛队', '請為每行選擇本組別參賽隊', 'Select a category entry for every row'],
  duplicateEntry: ['同一场次不可重复安排同一队伍', '同一場次不可重複安排同一隊伍', 'An entry cannot appear twice in a race'],
  duplicateLane: ['同一场次的航道不可重复', '同一場次的航道不可重複', 'Lanes must be unique within a race'],
  invalidLane: ['航道须为正整数或留空', '航道須為正整數或留空', 'Use a positive whole lane number or leave blank'],
  invalidRank: ['正式名次须为正整数或留空', '正式名次須為正整數或留空', 'Use a positive whole place or leave blank'],
  invalidTime: ['时间请填秒数或分:秒，最多3位小数且大于0', '時間請填秒數或分:秒，最多3位小數且大於0', 'Use positive seconds or m:ss, up to 3 decimal places'],
  incomplete: ['请补全裁判时间或正式名次；异常情况请选择DNS、DNF或DSQ', '請補全裁判時間或正式名次；異常情況請選擇DNS、DNF或DSQ', 'Enter an official time or place, or select DNS, DNF or DSQ'],
  tooLong: ['编号最多20字，备注最多255字', '編號最多20字，備註最多255字', 'Number: 20 characters maximum; notes: 255'],
  stage: ['阶段', '階段', 'Stage'], heat: ['组内轮次', '組內輪次', 'Heat within category'],
  noDelete: ['历史成绩不可删除；仅从未发布的草稿可移除。', '歷史成績不可刪除；僅從未發布的草稿可移除。', 'Historical results cannot be deleted. Only never-published drafts can be removed.'],
  sourceTime: ['例如 2:11.327', '例如 2:11.327', 'e.g. 2:11.327'],
  template: ['下载模板', '下載範本', 'Download template'], workbook: ['导入工作簿', '匯入活頁簿', 'Import workbook'], export: ['内部核对导出', '內部核對匯出', 'Export for internal checking'],
  importHelp: ['此操作将导入场次与草稿成绩，不会发布。请确认文件属于当前赛事。', '此操作將匯入場次與草稿成績，不會發布。請確認檔案屬於目前賽事。', 'This imports races and draft results, not publication. Confirm the file belongs to this event.'],
  lineup: ['坐舱名单', '座艙名單', 'Crew list'], removeRace: ['删除空场次', '刪除空場次', 'Delete empty race'],
  noHistory: ['暂无审计记录', '暫無審計記錄', 'No audit entries'], latest: ['读取最新记录', '讀取最新記錄', 'Read current records'],
  changeSchedule: ['赛程调整单独保存，不会保存或发布成绩。', '賽程調整單獨儲存，不會儲存或發布成績。', 'Schedule changes are saved separately; results are not saved or published.']
}
export const cardText = (locale, key) => labels[key] ? labels[key][locale === 'en' ? 2 : locale === 'zh-Hant' ? 1 : 0] : null
